import React, { useState, useEffect, useContext } from "react";

import ProductNewEditForm from "../ProductNewEditForm";
import { AuthContext } from "../../../context/AuthProvider";
import { AppContext } from "../../../context/AppProvider";
import { CUSTOM_KEYS, DEFAULT_DATA } from "../Constants";
import { fetchWithInterceptor } from "../../../utils/fetchWithInterceptor";

const ProductEditView = ({
  cin7Product,
  shopifyProduct,
  brands,
  categories,
  suppliers,
  unitsOfMeasure,
  resetValues,
}) => {
  const [values, setValues] = useState(DEFAULT_DATA);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const authContext = useContext(AuthContext);
  const appContext = useContext(AppContext);

  const handleSave = async () => {
    try {
      setIsSubmitting(true);
      const formData = new FormData();
      if (values.imageFile) {
        formData.append("image", values.imageFile);
      }
      const normalizedMetafields = {};
      for (const key in values.metafields) {
        normalizedMetafields[key] = values.metafields[key] ?? "";
      }
      // Ensure units_per_case has the correct value
      normalizedMetafields["units_per_case"] = values.unitsPerCase ?? "";

      formData.append(
        "payload",
        JSON.stringify({
          Cin7Id: cin7Product.productId,
          Shopify: {
            b2c: shopifyProduct?.b2c
              ? {
                  productId: shopifyProduct.b2c?.productId,
                  variantId: shopifyProduct.b2c?.variant?.variantId,
                }
              : null,
            b2b: shopifyProduct?.b2b
              ? {
                  productId: shopifyProduct.b2b?.productId,
                  variantId: shopifyProduct.b2b?.variant?.variantId,
                }
              : null,
          },
          Name: values.title,
          SKU: values.sku,
          Barcode: values.barcode,
          BrandName: values.brand,
          CategoryName: values.category,
          Description: values.description,
          Weight: values.weight,
          UnitOfMeasure: values.unitOfMeasure,
          SupplierID: values.supplier,
          SupplierInventoryCode: values.unitsPerCase,
          Tags: values.tags,
          LatestPrice: Number(values.latestPrice),
          FixedPrice: Number(values.fixedPrice),
          AverageCost: Number(values.averageCost),
          Status: values.status || "",
          ShopifyStatus: values.shopifyStatus || "",
          price1: Number(values.price1) || 0,
          price2: Number(values.price2) || 0,
          price3: Number(values.price3) || 0,
          PriceTiers: {
            Tier1: {
              TierNumber: 1,
              Price: values.price1 || 0,
              MarkupType: values.markupType1,
              UsePriceType: values.usePriceType1,
              MarkupValue: values.markupValue1 || null,
              Calculated: values.calculated1,
            },
            Tier2: {
              TierNumber: 2,
              Price: values.price2 || 0,
              MarkupType: values.markupType2,
              UsePriceType: values.usePriceType2,
              MarkupValue: values.markupValue2 || null,
              Calculated: values.calculated2,
            },
            Tier3: {
              TierNumber: 3,
              Price: values.price3 || 0,
              MarkupType: values.markupType3,
              UsePriceType: values.usePriceType3,
              MarkupValue: values.markupValue3 || null,
              Calculated: values.calculated3,
            },
          },
          ContinueSelling: values.continueSelling ? "CONTINUE" : "DENY",
          Metafields: normalizedMetafields,
          Publications: values.publications,
          removeImage: values.removeImage === true,
        }),
      );

      const res = await fetchWithInterceptor(`/api/products/update`, {
        method: "POST",
        headers: { "x-access-token": authContext.getToken() },
        body: formData,
      });

      if (!res.ok) {
        const errText = await res.text();
        throw new Error(errText || "Error updating product");
      }

      appContext.showMessage("Product updated successfully!", "success");
      setIsSubmitting(false);

      resetValues();
    } catch (error) {
      setIsSubmitting(false);
      appContext.showMessage(
        error.message || "Error updating product",
        "error",
      );
    }
  };

  const handleDismiss = () => {
    resetValues();
  };

  useEffect(() => {
    if (!cin7Product || !shopifyProduct?.b2c) return;

    const baseShopifyProduct = shopifyProduct.b2c;

    const filteredMetas = baseShopifyProduct.metafields?.filter(
      (m) =>
        CUSTOM_KEYS.includes(m.key) ||
        (m.namespace === "netsuite" && !CUSTOM_KEYS.includes(m.key)),
    );

    const getMetafield = (key) =>
      filteredMetas?.find((m) => m.key === key)?.value ?? "";

    const getMetafieldArray = (key) => {
      const raw = getMetafield(key);
      try {
        const parsed = raw ? JSON.parse(raw) : [];
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    };

    // rating_notes is a json metafield: { [critic key]: description }.
    const getRatingNotes = () => {
      try {
        const parsed = JSON.parse(getMetafield("rating_notes") || "{}");
        return parsed && !Array.isArray(parsed) && typeof parsed === "object" ? parsed : {};
      } catch {
        return {};
      }
    };

    const tier1 =
      cin7Product.markupPrices?.find((m) => m.TierNumber === 1) || {};
    const tier2 =
      cin7Product.markupPrices?.find((m) => m.TierNumber === 2) || {};
    const tier3 =
      cin7Product.markupPrices?.find((m) => m.TierNumber === 3) || {};

    const isCalc1 = ["A", "P"].includes(tier1.MarkupType);
    const isCalc2 = ["A", "P"].includes(tier2.MarkupType);
    const isCalc3 = ["A", "P"].includes(tier3.MarkupType);

    const publications = Array.isArray(baseShopifyProduct.publications)
      ? [...baseShopifyProduct.publications]
      : [];

    if (shopifyProduct?.b2b && !publications.includes("b2b_onlineStore")) {
      publications.push("b2b_onlineStore");
    }

    setValues({
      title: cin7Product.productName || "",
      sku: cin7Product.sku || "",
      barcode: cin7Product.barcode || "",
      brand: cin7Product.brand || "",
      category: cin7Product.category || "",
      status: cin7Product.status || "",
      description: cin7Product.description || "",
      weight: cin7Product.weight || "",
      supplier: cin7Product.supplierId || "",
      unitsPerCase: cin7Product.supplierInventoryCode || "",
      unitOfMeasure: cin7Product.unitOfMeasure || "",
      tags: cin7Product.tags || [],
      latestPrice: cin7Product.latestPrice ?? "",
      fixedPrice: cin7Product.fixedPrice ?? "",
      averageCost: cin7Product.averageCost,
      price1: isCalc1 ? "" : cin7Product.priceTier1,
      price2: isCalc2 ? "" : cin7Product.priceTier2,
      price3: isCalc3 ? "" : cin7Product.priceTier3,
      calculated1: isCalc1,
      calculated2: isCalc2,
      calculated3: isCalc3,
      markupType1: isCalc1 ? tier1.MarkupType : "",
      markupType2: isCalc2 ? tier2.MarkupType : "",
      markupType3: isCalc3 ? tier3.MarkupType : "",
      usePriceType1: isCalc1 ? tier1.UsePriceType : "",
      usePriceType2: isCalc2 ? tier2.UsePriceType : "",
      usePriceType3: isCalc3 ? tier3.UsePriceType : "",
      markupValue1: isCalc1 ? tier1.MarkupValue : "",
      markupValue2: isCalc2 ? tier2.MarkupValue : "",
      markupValue3: isCalc3 ? tier3.MarkupValue : "",
      shopifyStatus: baseShopifyProduct.status || "",
      continueSelling:
        baseShopifyProduct.variant?.inventoryPolicy === "CONTINUE",
      image: baseShopifyProduct.image || "",
      publications,
      metafields: {
        limit_prod: getMetafield("limit_prod"),
        expensive_product_limit: getMetafield("expensive_product_limit"),
        vintage: getMetafield("vintage"),
        container_deposit_sku: getMetafield("container_deposit_sku"),
        type: getMetafield("type"),
        subtype: getMetafield("subtype"),
        subsubtype: getMetafield("subsubtype"),
        size: getMetafield("size"),
        presentation: getMetafield("presentation"),
        container: getMetafield("container"),
        country: getMetafield("country"),
        region: getMetafield("region"),
        subregion: getMetafield("subregion"),
        abv: getMetafield("abv"),
        provider: getMetafield("provider"),
        units_per_case: getMetafield("units_per_case"),
        james_suckling: getMetafield("james_suckling"),
        robert_parker: getMetafield("robert_parker"),
        jeb_dunnuck: getMetafield("jeb_dunnuck"),
        tim_atkin: getMetafield("tim_atkin"),
        wine_spectator: getMetafield("wine_spectator"),
        wine_enthusiast: getMetafield("wine_enthusiast"),
        wine_and_spirits: getMetafield("wine_and_spirits"),
        vinous: getMetafield("vinous"),
        allen_meadows_burghound: getMetafield("allen_meadows_burghound"),
        whisky_advocate: getMetafield("whisky_advocate"),
        decanter: getMetafield("decanter"),
        best_rate: getMetafield("best_rate"),
        rating_notes: getRatingNotes(),
        video_url: getMetafield("video_url"),
        primary_classification: getMetafieldArray("primary_classification"),
        secondary_classification: getMetafieldArray("secondary_classification"),
        producer: getMetafield("producer"),
      },
    });
  }, [cin7Product, shopifyProduct]);

  return (
    <ProductNewEditForm
      isEditing
      cin7Product={cin7Product}
      shopifyProduct={shopifyProduct?.b2c}
      shopifyB2bProduct={shopifyProduct?.b2b} 
      brands={brands}
      categories={categories}
      suppliers={suppliers}
      unitsOfMeasure={unitsOfMeasure}
      values={values}
      setValues={setValues}
      handleSave={handleSave}
      handleDismiss={handleDismiss}
      isSubmitting={isSubmitting}
    />
  );
};

export default ProductEditView;
